import { useState } from "react";
import { useForm } from "@mantine/form";
import {
  Alert,
  Container,
  PasswordInput,
  Text,
  Paper,
  Group,
  Button,
  Stack,
} from "@mantine/core";
import { useStore } from "../store";
import { resetPasswordWithToken } from "./authApi";
import { Link, useLocation } from "wouter";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";

export default function ResetPassword({ params }) {
  const { t } = useTranslation("Auth");
  const [, setLocation] = useLocation();
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const authToken = useStore((state) => state.authToken);
  const form = useForm({
    initialValues: {
      token: params.token,
      newPassword: "",
    },
    validate: {
      newPassword: (val) => (val.length < 8 ? t("invalid_password") : null),
      // emailAddress: (val) => (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val) ? null : "Invalid email"),
    },
  });

  if (authToken) {
    return (
      <Container size="sm" mt="xl">
        <Paper radius="md" p="xl" withBorder>
          <Text size="lg" fw={500}>
            {t("you_are_logged_in")}
          </Text>
          <Button component={Link} href="/" radius="xl" mt="lg" variant="light">
            {t("continue", "Continue")}
          </Button>
        </Paper>
      </Container>
    );
  }

  const errorIcon = <IconInfoCircle />;

  return (
    <Container size="sm" mt="xl">
      {errorMessage && (
        <Alert
          variant="light"
          color="red"
          title={t("reset_failed", "Reset failed")}
          mb="lg"
          icon={errorIcon}
        >
          {errorMessage}
        </Alert>
      )}

      <Paper radius="md" p="xl" withBorder>
        <Text size="lg" mb="lg" fw={500}>
          {t("set_new_password", "Set a new password")}
        </Text>
        {/* 
        <Divider label="Reset Password" labelPosition="center" my="lg" /> */}

        <form
          onSubmit={form.onSubmit((values) => {
            setIsFetching(true);
            setErrorMessage(null);

            resetPasswordWithToken({ ...values })
              .then(() => {
                setIsFetching(false);
                notifications.show({
                  color: "green",
                  title: t("password_saved", "Password saved"),
                  message: t("password_saved_message", "Log in with your new password."),
                });
                setLocation("/auth/login");
              })
              .catch((err) => {
                setIsFetching(false);
                setErrorMessage(
                  err.status === 404 || err.status === 400
                    ? t(
                        "reset_link_invalid",
                        "This link has expired or is not valid. Request a new one on the password recovery page.",
                      )
                    : err.status === 429
                      ? t(
                          "activation_rate_limited",
                          "Too many attempts. Please wait a minute and try again.",
                        )
                      : t(
                          "reset_error",
                          "We could not save your password. Please try again in a moment.",
                        ),
                );
              });
          })}
        >
          <Stack>
            <PasswordInput
              required
              disabled={isFetching}
              autoComplete="new-password"
              label={t("new_password")}
              placeholder={t("choose_password", "Choose a password (at least 8 characters)")}
              value={form.values.newPassword}
              onChange={(event) =>
                form.setFieldValue("newPassword", event.currentTarget.value)
              }
              error={form.errors.newPassword}
              radius="md"
            />

            <Group justify="flex-end" mt="xl">
              <Button type="submit" radius="xl" loading={isFetching}>
                {t("save_password", "Save password")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
